<template>
  <div class="modern-audit-wrap">
    <!-- Hero Section -->
    <section class="hero-section">
      <div class="container hero-content">
        <div class="hero-text">
          <span class="badge">{{ $t('productionBrain.hero.badge') }}</span>
          <h1 class="hero-title">{{ $t('productionBrain.hero.title') }}</h1>
          <p class="hero-description">{{ $t('productionBrain.hero.desc1') }}
          </p>
           <p class="hero-description">{{ $t('productionBrain.hero.desc2') }}
          </p>
          <div class="hero-actions">
            <router-link to="/buy" class="btn btn-primary">{{ $t('productionBrain.hero.btnShop') }}</router-link>
            <button type="button" class="btn btn-secondary" @click="scrollToNextSection">{{ $t('productionBrain.hero.btnExplore') }}</button>
          </div>
        </div>
        <div class="hero-visual">
          <div class="download-shortcut-card" @click="downloadShortcut">
            <div class="shortcut-icon">
              <img :src="$brand.logo" :alt="$t('brand.logoAlt')" class="shortcut-logo" />
            </div>
            <h3>{{ $t('productionBrain.downloadShortcut.title') }}</h3>
            <p>{{ $t('productionBrain.downloadShortcut.desc') }}</p>
            <div class="btn btn-primary shortcut-btn">{{ $t('productionBrain.downloadShortcut.btn') }}</div>
          </div>
        </div>
      </div>
    </section>

    <!-- Stats Section -->
    <section ref="nextSection" class="stats-section">
      <div class="container stats-grid">
        <div class="stat-item" v-for="(stat, index) in stats" :key="index">
          <div class="stat-value">{{ stat.value }}</div>
          <div class="stat-label">{{ stat.label }}</div>
          <p class="stat-desc">{{ stat.desc }}</p>
        </div>
      </div>
    </section>

    <!-- Solutions / Use Cases Section -->
    <section class="solutions-section">
      <div class="container">
        <div class="section-header">
          <h2>{{ $t('productionBrain.solutions.header.title') }}</h2>
          <p>{{ $t('productionBrain.solutions.header.desc') }}</p>
        </div>

        <div class="solutions-grid">
          <div class="solution-card" v-for="(solution, index) in solutions" :key="index">
            <div class="sol-icon" v-html="solution.icon"></div>
            <h4>{{ solution.title }}</h4>
            <p>{{ solution.description }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Core Features Section (Expanded) -->
    <section class="features-section">
      <div class="container">
        <div class="section-header">
          <h2>{{ $t('productionBrain.features.header.title') }}</h2>
          <p>{{ $t('productionBrain.features.header.desc') }}</p>
        </div>
        <div class="feature-grid">
          <div class="feature-card" v-for="(feature, index) in features" :key="index">
            <div class="feature-icon" v-html="feature.icon"></div>
            <h3>{{ feature.title }}</h3>
            <p>{{ feature.description }}</p>
            <ul class="feature-list">
              <li v-for="(item, i) in feature.list" :key="i">{{ item }}</li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <!-- Integrations & Ecosystem -->
    <section class="integration-section">
      <div class="container integration-content">
        <div class="integration-text">
          <h2>{{ $t('productionBrain.integration.title') }}</h2>
          <p>{{ $t('productionBrain.integration.desc') }}</p>
          <ul class="integration-benefits">
            <li><strong>{{ $t('productionBrain.integration.benefits.digitalization.label') }}</strong> {{ $t('productionBrain.integration.benefits.digitalization.text') }}</li>
            <li><strong>{{ $t('productionBrain.integration.benefits.sync.label') }}</strong> {{ $t('productionBrain.integration.benefits.sync.text') }}</li>
            <li><strong>{{ $t('productionBrain.integration.benefits.data.label') }}</strong> {{ $t('productionBrain.integration.benefits.data.text') }}</li>
          </ul>
        </div>
        <div class="integration-cloud">
          <div class="node center-node">{{ $t('productionBrain.integration.nodes.center') }}</div>
          <div class="node orbit-1">{{ $t('productionBrain.integration.nodes.offline') }}</div>
          <div class="node orbit-2">{{ $t('productionBrain.integration.nodes.portal') }}</div>
          <div class="node orbit-3">{{ $t('productionBrain.integration.nodes.templates') }}</div>
          <div class="node orbit-4">{{ $t('productionBrain.integration.nodes.workflows') }}</div>
          <div class="node orbit-5">{{ $t('productionBrain.integration.nodes.permissions') }}</div>
          <div class="node orbit-6">{{ $t('productionBrain.integration.nodes.library') }}</div>
        </div>
      </div>
    </section>

    <!-- Workflow Section -->
    <section class="workflow-section">
      <div class="container">
        <div class="section-header">
          <h2>{{ $t('productionBrain.workflow.header.title') }}</h2>
          <p>{{ $t('productionBrain.workflow.header.desc') }}</p>
        </div>
        <div class="workflow-container">
          <div class="workflow-step" v-for="(step, index) in workflows" :key="index">
            <div class="step-circle">{{ index + 1 }}</div>
            <div class="step-content">
              <h4>{{ step.title }}</h4>
              <p>{{ step.description }}</p>
            </div>
            <div class="step-connector" v-if="index < workflows.length - 1"></div>
          </div>
        </div>
      </div>
    </section>

    <!-- Value Proposition / Security -->
    <section class="value-section">
      <div class="container value-content">
        <div class="value-text">
          <h2>{{ $t('productionBrain.value.title') }}</h2>
          <p class="value-lead">{{ $t('productionBrain.value.lead') }}</p>
          <div class="value-points">
            <div class="v-point" v-for="(vp, index) in valueProps" :key="index">
              <div class="vp-icon">
                <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" stroke-width="2" fill="none"><polyline points="20 6 9 17 4 12"></polyline></svg>
              </div>
              <div class="vp-text">
                <strong>{{ vp.title }}</strong>
                <p>{{ vp.desc }}</p>
              </div>
            </div>
          </div>
        </div>
        <div class="value-visual">
          <div class="shield-container">
            <div class="shield-outer"></div>
            <div class="shield-inner">
              <svg viewBox="0 0 24 24" width="60" height="60" stroke="#fff" stroke-width="1.5" fill="none">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
              </svg>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Bottom CTA -->
    <section class="bottom-cta">
      <div class="container cta-container">
        <h2>{{ $t('productionBrain.cta.title') }}</h2>
        <p>{{ $t('productionBrain.cta.desc') }}</p>
        <router-link to="/buy" class="btn btn-primary btn-large">{{ $t('productionBrain.cta.btn') }}</router-link>
      </div>
    </section>


  </div>
</template>

<script>


export default {
  name: 'ModernAuditProductionBrain',
  components: {

  },
  computed: {
    stats() {
      return [
        { value: '>30%', label: this.$t('productionBrain.stats.timeSaved.label'), desc: this.$t('productionBrain.stats.timeSaved.desc') },
        { value: 'Real-Time', label: this.$t('productionBrain.stats.riskVisibility.label'), desc: this.$t('productionBrain.stats.riskVisibility.desc') },
        { value: '100+', label: this.$t('productionBrain.stats.integrations.label'), desc: this.$t('productionBrain.stats.integrations.desc') },
        { value: 'Zero', label: this.$t('productionBrain.stats.surpriseFindings.label'), desc: this.$t('productionBrain.stats.surpriseFindings.desc') }
      ]
    },
    solutions() {
      const items = this.$t('productionBrain.solutions.items');
      const icons = [
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="13" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>',
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>',
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="12" y1="1" x2="12" y2="23"></line><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path></svg>',
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"></circle><line x1="2" y1="12" x2="22" y2="12"></line><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path></svg>'
      ];
      return items.map((item, index) => ({ ...item, icon: icons[index] }));
    },
    features() {
      const items = this.$t('productionBrain.features.items');
      const icons = [
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="12 2 2 7 12 12 22 7 12 2"></polygon><polyline points="2 17 12 22 22 17"></polyline><polyline points="2 12 12 17 22 12"></polyline></svg>',
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="17 8 12 3 7 8"></polyline><line x1="12" y1="3" x2="12" y2="15"></line></svg>',
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>',
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>',
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><line x1="3" y1="9" x2="21" y2="9"></line><line x1="9" y1="21" x2="9" y2="9"></line></svg>',
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="20" x2="18" y2="10"></line><line x1="12" y1="20" x2="12" y2="4"></line><line x1="6" y1="20" x2="6" y2="14"></line></svg>'
      ];
      return items.map((item, index) => ({ ...item, icon: icons[index] }));
    },
    workflows() {
      return this.$t('productionBrain.workflow.items');
    },
    valueProps() {
      return this.$t('productionBrain.value.props');
    }
  },
  data() {
    return {}
  },
  methods: {
    scrollToNextSection() {
      const section = this.$refs.nextSection;
      if (!section) return;
      section.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'start'
      });
    },
    downloadShortcut() {
      const content = `[InternetShortcut]\nURL=${window.location.origin}/\nIconIndex=0`;
      const blob = new Blob([content], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${this.$t('buy.payment.result.shortcutFileName')}.url`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    }
  }
}
</script>

<style lang="scss" scoped>
/* Modern SaaS Theme Variables - Refined */
$primary-color: #0f62fe; /* Deep Trust Blue */
$primary-hover: #0043ce;
$secondary-color: #1e293b;
$text-dark: #0f172a;
$text-muted: #475569;
$bg-light: #f8fafc;
$white: #ffffff;
$border-color: #cbd5e1;

.modern-audit-wrap {
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen-Sans, Ubuntu, Cantarell, "Helvetica Neue", sans-serif;
  color: $text-dark;
  background-color: $white;
  line-height: 1.6;
}

.container {
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 24px;
}

/* Common UI */
.badge {
  display: inline-block;
  background-color: rgba($primary-color, 0.1);
  color: $primary-color;
  padding: 6px 14px;
  border-radius: 999px;
  font-size: 0.85rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 24px;
}

.btn {
  font-family: inherit;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 14px 28px;
  border-radius: 6px;
  font-weight: 600;
  font-size: 1.05rem;
  transition: all 0.2s ease;
  cursor: pointer;
  text-decoration: none;
  

  &-primary {
    background: linear-gradient(134deg, #0649b8 1%, #0a7fd7 41%, #03d2c5 99%);
    color: $white;
    border: none;
    box-shadow: 0 4px 14px 0 rgba($primary-color, 0.39);

    &:hover {
      // background-color: $primary-hover;
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba($primary-color, 0.23);
    }
  }

  &-secondary {
    background-color: transparent;
    color: $text-dark;
    border: 1px solid #94a3b8;

    &:hover {
      background-color: #f1f5f9;
      border-color: $text-dark;
    }
  }

  &.btn-large {
    padding: 18px 36px;
    font-size: 1.2rem;
  }
}

.stats-section {
  scroll-margin-top: 80px;
}

.section-header {
  text-align: center;
  margin-bottom: 60px;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;

  h2 {
    font-size: 2.5rem;
    font-weight: 800;
    margin-bottom: 16px;
    color: $secondary-color;
    letter-spacing: -0.02em;
  }

  p {
    font-size: 1.15rem;
    color: $text-muted;
  }
}

/* Hero Section */
.hero-section {
  padding: 100px 0 60px;
  background: linear-gradient(135deg, #f0f4ff 0%, #ffffff 100%);
  position: relative;
  overflow: hidden;

  .hero-content {
    display: flex;
    align-items: center;
    gap: 60px;
    margin-bottom: 80px;

    @media (max-width: 992px) {
      flex-direction: column;
      text-align: center;
    }
  }

  .hero-text {
    flex: 1.1;

    .hero-title {
      font-size: 3.8rem;
      font-weight: 900;
      line-height: 1.1;
      margin-bottom: 24px;
      letter-spacing: -0.03em;
      color: #020617;

      @media (max-width: 768px) {
        font-size: 2.8rem;
      }
    }

    .hero-description {
      font-size: 1.25rem;
      color: $text-muted;
      margin-bottom: 40px;
      line-height: 1.7;
    }

    .hero-actions {
      display: flex;
      gap: 16px;

      @media (max-width: 992px) {
        justify-content: center;
      }
      @media (max-width: 576px) {
        flex-direction: column;
      }
    }
  }

  .hero-visual {
    flex: 0.9;
    width: 100%;
    perspective: 1000px;
  }

  /* Enhanced Dashboard Mockup */
  .dashboard-mockup {
    background: $white;
    border-radius: 12px;
    box-shadow: 0 30px 60px -15px rgba(0, 0, 0, 0.2);
    border: 1px solid $border-color;
    overflow: hidden;
    height: 420px;
    display: flex;
    flex-direction: column;
    transform: rotateY(-8deg) rotateX(5deg) scale(0.95);
    transition: transform 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275);
    transform-origin: right center;

    &:hover {
      transform: rotateY(0deg) rotateX(0deg) scale(1);
    }

    .mockup-header {
      background: #f1f5f9;
      padding: 12px 16px;
      display: flex;
      gap: 8px;
      border-bottom: 1px solid $border-color;

      .dot {
        width: 12px;
        height: 12px;
        border-radius: 50%;
        &.red { background: #ff5f56; }
        &.yellow { background: #ffbd2e; }
        &.green { background: #27c93f; }
      }
    }

    .mockup-body {
      display: flex;
      flex: 1;
      padding: 16px;
      gap: 16px;
      background: #fafaf9;
    }

    .mockup-sidebar {
      width: 50px;
      background: #e2e8f0;
      border-radius: 6px;
    }

    .mockup-content {
      flex: 1;
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    .mockup-top-bar {
      height: 36px;
      background: $white;
      border-radius: 6px;
      box-shadow: 0 1px 2px rgba(0,0,0,0.05);
      display: flex;
      align-items: center;
      padding: 0 10px;

      .search-bar-mock {
        width: 120px;
        height: 16px;
        background: #f1f5f9;
        border-radius: 4px;
      }
    }

    .mockup-chart-area {
      flex: 1;
      background: $white;
      border-radius: 6px;
      box-shadow: 0 1px 3px rgba(0,0,0,0.1);
      display: flex;
      align-items: center;
      justify-content: space-around;
      padding: 20px;

      .chart-circle {
        width: 100px;
        height: 100px;
        border-radius: 50%;
        border: 15px solid #e2e8f0;
        border-top-color: $primary-color;
        border-right-color: $primary-color;
        transform: rotate(45deg);
      }

      .chart-bars {
        display: flex;
        align-items: flex-end;
        gap: 10px;
        height: 100px;

        .bar {
          width: 24px;
          background: #94a3b8;
          border-radius: 4px 4px 0 0;
          &:nth-child(2) { background: $primary-color; }
        }
      }
    }

    .mockup-row {
      display: flex;
      gap: 12px;
      height: 70px;
    }

    .mockup-card-mini {
      flex: 1;
      background: $white;
      border-radius: 6px;
      box-shadow: 0 1px 3px rgba(0,0,0,0.1);
    }
  }

  .trusted-by {
    text-align: center;
    border-top: 1px solid #e2e8f0;
    padding-top: 40px;

    .trusted-label {
      font-size: 0.8rem;
      font-weight: 700;
      color: #64748b;
      margin-bottom: 20px;
      letter-spacing: 1px;
    }

    .logo-strip {
      display: flex;
      justify-content: center;
      gap: 60px;
      flex-wrap: wrap;
      opacity: 0.6;
      filter: grayscale(100%);

      .mock-logo {
        font-family: inherit;
        font-weight: 900;
        font-size: 1.5rem;
        color: #1e293b;
      }
    }
  }

  /* Shortcut Card */
  .download-shortcut-card {
    background: #ffffff;
    border-radius: 16px;
    padding: 40px;
    text-align: center;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.08);
    border: 2px dashed #cbd5e1;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;
    height: 100%;
    min-height: 300px;

    &:hover {
      transform: translateY(-5px);
      box-shadow: 0 25px 50px rgba(15, 98, 254, 0.15);
      border-color: #0f62fe;
      
      .shortcut-icon {
        transform: scale(1.1);
        color: #0f62fe;
      }
    }

    .shortcut-icon {
      color: #475569;
      transition: all 0.3s ease;
      margin-bottom: 10px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .shortcut-logo {
      width: 64px;
      height: 64px;
      object-fit: contain;
    }

    h3 {
      font-size: 1.5rem;
      color: #0f172a;
      margin: 0;
      font-weight: 700;
    }

    p {
      color: #475569;
      margin: 0 0 10px;
      font-size: 1.1rem;
    }
    
    .shortcut-btn {
      padding: 10px 24px;
      font-size: 1rem;
    }
  }
}

/* Stats Section */
.stats-section {
  padding: 80px 0;
  background: $secondary-color;
  color: $white;

  .stats-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 30px;
    text-align: center;

    @media (max-width: 992px) {
      grid-template-columns: repeat(2, 1fr);
      gap: 50px;
    }
    @media (max-width: 480px) {
      grid-template-columns: 1fr;
    }

    .stat-value {
      font-size: 3rem;
      font-weight: 800;
      color: #60a5fa;
      margin-bottom: 8px;
    }

    .stat-label {
      font-size: 1.25rem;
      color: $white;
      font-weight: 600;
      margin-bottom: 12px;
    }

    .stat-desc {
      color: #94a3b8;
      font-size: 0.95rem;
      line-height: 1.5;
    }
  }
}

/* Solutions Section */
.solutions-section {
  padding: 100px 0;
  background: $white;

  .solutions-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 30px;

    .solution-card {
      background: #f8fafc;
      border: 1px solid #e2e8f0;
      padding: 30px;
      border-radius: 12px;
      text-align: left;
      transition: all 0.3s ease;

      &:hover {
        background: $white;
        box-shadow: 0 10px 25px rgba(0,0,0,0.08);
        border-color: #cbd5e1;
        transform: translateY(-5px);
      }

      .sol-icon {
        width: 40px;
        height: 40px;
        color: $primary-color;
        margin-bottom: 20px;
        margin: 0 auto;
      }

      h4 {
        font-size: 1.25rem;
        margin-bottom: 12px;
        color: $text-dark;
      }

      p {
        color: $text-muted;
        font-size: 0.95rem;
      }
    }
  }
}

/* Features Section - Expanded 6 Card Grid */
.features-section {
  padding: 100px 0;
  background: #f1f5f9;

  .feature-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 30px;

    @media (max-width: 992px) {
      grid-template-columns: repeat(2, 1fr);
    }
    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }

    .feature-card {
      background: $white;
      padding: 40px 30px;
      border-radius: 12px;
      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
      border: 1px solid transparent;
      transition: all 0.3s ease;

      &:hover {
        transform: translateY(-5px);
        box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
        border-color: rgba($primary-color, 0.2);
      }
    }

    .feature-icon {
      width: 50px;
      height: 50px;
      color: $white;
      background: $primary-color;
      padding: 12px;
      border-radius: 12px;
      margin: 24px auto;
      box-shadow: 0 4px 10px rgba($primary-color, 0.3);
    }

    h3 {
      font-size: 1.4rem;
      margin-bottom: 16px;
      color: $text-dark;
      text-align: center;
      font-weight: 700;
    }

    p {
      color: $text-muted;
      margin-bottom: 24px;
      min-height: 85px;
    }

    .feature-list {
      list-style: none;
      padding: 0;
      margin: 0;
      border-top: 1px solid #e2e8f0;
      padding-top: 20px;

      li {
        position: relative;
        padding-left: 24px;
        margin-bottom: 12px;
        color: #475569;
        font-size: 0.95rem;
        font-weight: 500;

        &::before {
          content: "";
          position: absolute;
          left: 0;
          top: 45%;
          width: 6px;
          height: 6px;
          background: $primary-color;
          border-radius: 50%;
        }
      }
    }
  }
}

/* Integrations Section */
.integration-section {
  padding: 100px 0;
  background: $white;
  border-bottom: 1px solid #e2e8f0;

  .integration-content {
    display: flex;
    align-items: center;
    gap: 80px;

    @media (max-width: 992px) {
      flex-direction: column;
    }
  }

  .integration-text {
    flex: 1;

    h2 {
      font-size: 2.5rem;
      font-weight: 800;
      margin-bottom: 24px;
    }

    p {
      font-size: 1.15rem;
      color: $text-muted;
      margin-bottom: 30px;
    }

    .integration-benefits {
      list-style: none;
      padding: 0;
      li {
        margin-bottom: 16px;
        font-size: 1.05rem;
        position: relative;
        padding-left: 30px;
        color: #334155;

        &::before {
          content: '✓';
          position: absolute;
          left: 0;
          color: #10b981;
          font-weight: bold;
          font-size: 1.2rem;
        }
      }
    }
  }

  .integration-cloud {
    flex: 1;
    height: 400px;
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;

    .node {
      position: absolute;
      background: $white;
      border: 1px solid #e2e8f0;
      padding: 10px 20px;
      border-radius: 30px;
      font-weight: 700;
      box-shadow: 0 4px 6px rgba(0,0,0,0.05);
      color: $text-dark;
      transition: transform 0.3s;

      &:hover {
        transform: scale(1.1);
        border-color: $primary-color;
        z-index: 10;
      }
    }

    .center-node {
      background: linear-gradient(134deg, #0649b8 1%, #0a7fd7 41%, #03d2c5 99%);
      color: $white;
      border: none;
      padding: 15px 30px;
      font-size: 1.2rem;
      box-shadow: 0 10px 20px rgba($primary-color, 0.3);
      z-index: 5;
    }

    /* Scattered placements simulating an orbit */
    .orbit-1 { top: 15%; left: 20%; }
    .orbit-2 { top: 10%; right: 25%; }
    .orbit-3 { top: 40%; left: 5%; }
    .orbit-4 { top: 50%; right: 5%; }
    .orbit-5 { bottom: 15%; left: 5%; }
    .orbit-6 { bottom: 20%; right: 20%; }
  }
}

/* Workflow Section */
.workflow-section {
  padding: 100px 0;
  background: #f8fafc;

  .workflow-container {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    position: relative;
    max-width: 1000px;
    margin: 0 auto;

    @media (max-width: 768px) {
      flex-direction: column;
      gap: 40px;
      padding-left: 20px;
    }
  }

  .workflow-step {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    position: relative;
    padding: 0 20px;
    

    @media (max-width: 768px) {
      flex-direction: row;
      text-align: left;
      padding: 0;
    }

    .step-circle {
      width: 50px;
      height: 50px;
      background: $white;
      color: $primary-color;
      border: 3px solid $primary-color;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 1.25rem;
      font-weight: 800;
      margin-bottom: 24px;
      z-index: 2;
        border: 4px solid transparent;
  border-image: linear-gradient(134deg, #0649b8 1%, #0a7fd7 41%, #03d2c5 99%);
  border-image-slice: 1;

      @media (max-width: 768px) {
        margin-bottom: 0;
        margin-right: 24px;
        min-width: 50px;
      }
    }

    .step-content {
      h4 {
        font-size: 1.2rem;
        margin-bottom: 12px;
        font-weight: 700;
        color: $text-dark;
      }
      p {
        color: $text-muted;
        font-size: 0.95rem;
        line-height: 1.5;
      }
    }

    .step-connector {
      position: absolute;
      top: 25px;
      right: -50%;
      width: 100%;
      height: 2px;
      background: #cbd5e1;
      z-index: 1;

      &::after {
        content: '';
        position: absolute;
        right: 0;
        top: -4px;
        width: 10px;
        height: 10px;
        border-top: 2px solid #cbd5e1;
        border-right: 2px solid #cbd5e1;
        transform: rotate(45deg);
      }

      @media (max-width: 768px) {
        display: none;
      }
    }
  }
}

/* Value Proposition / Security */
.value-section {
  padding: 100px 0;
  background: #020617; /* Very dark slate */
  color: $white;

  .value-content {
    display: flex;
    align-items: center;
    gap: 80px;

    @media (max-width: 992px) {
      flex-direction: column;
    }
  }

  .value-text {
    flex: 1;

    h2 {
      font-size: 2.5rem;
      margin-bottom: 24px;
      color: $white;
    }

    .value-lead {
      font-size: 1.25rem;
      color: #94a3b8;
      margin-bottom: 40px;
      line-height: 1.6;
    }

    .value-points {
      display: flex;
      flex-direction: column;
      gap: 30px;

      .v-point {
        display: flex;
        gap: 20px;

        .vp-icon {
          color: #60a5fa;
          background: rgba(96, 165, 250, 0.1);
          padding: 10px;
          border-radius: 8px;
          height: fit-content;
        }

        strong {
          font-size: 1.15rem;
          display: block;
          margin-bottom: 8px;
        }

        p {
          color: #94a3b8;
          margin: 0;
          line-height: 1.5;
        }
      }
    }
  }

  .value-visual {
    flex: 1;
    display: flex;
    justify-content: center;
    align-items: center;

    /* Security Shield Animation */
    .shield-container {
      position: relative;
      width: 300px;
      height: 300px;
      display: flex;
      justify-content: center;
      align-items: center;

      .shield-outer {
        position: absolute;
        width: 100%;
        height: 100%;
        border-radius: 50%;
        border: 2px dashed rgba(96, 165, 250, 0.3);
        animation: rotate 20s linear infinite;
      }

      .shield-inner {
        width: 150px;
        height: 150px;
        background: linear-gradient(135deg, #1d4ed8, #3b82f6);
        border-radius: 50%;
        display: flex;
        justify-content: center;
        align-items: center;
        box-shadow: 0 0 40px rgba(59, 130, 246, 0.4);
        position: relative;

        &::after {
          content: '';
          position: absolute;
          top: -10px; bottom: -10px; left: -10px; right: -10px;
          border-radius: 50%;
          border: 1px solid rgba(59, 130, 246, 0.5);
          animation: pulse 2s ease-in-out infinite alternate;
        }
      }
    }
  }
}

/* Bottom CTA */
.bottom-cta {
  padding: 80px 0;
  background: $primary-color;
  text-align: center;
  color: $white;

  h2 {
    font-size: 2.5rem;
    font-weight: 800;
    margin-bottom: 20px;
  }

  p {
    font-size: 1.25rem;
    margin-bottom: 40px;
    color: rgba(255,255,255,0.9);
    max-width: 600px;
    margin-left: auto;
    margin-right: auto;
  }
}

/* Mobile refinements */
@media (max-width: 992px) {
  .hero-section .hero-visual {
    max-width: 380px;
    margin: 0 auto;
  }
}

@media (max-width: 768px) {
  .hero-section,
  .solutions-section,
  .features-section,
  .integration-section,
  .workflow-section,
  .value-section {
    padding: 60px 0;
  }

  .stats-section,
  .bottom-cta {
    padding: 50px 0;
  }

  .hero-section .hero-content {
    margin-bottom: 40px;
  }

  .integration-section .integration-cloud {
    height: 340px;
    width: 100%;
    max-width: 420px;
    margin: 0 auto;
  }

  .value-visual .shield-container {
    width: 220px;
    height: 220px;

    .shield-inner {
      width: 110px;
      height: 110px;
    }
  }
}


/* Keyframes */
@keyframes rotate {
  100% { transform: rotate(360deg); }
}

@keyframes pulse {
  0% { transform: scale(0.95); opacity: 0.5; }
  100% { transform: scale(1.1); opacity: 1; }
}
</style>
